var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { useEvent, useGetRecordRepresentation, useResourceContext, useTranslate, } from 'react-admin';
import { DropIndicator } from './DropIndicator';
import NodeTitle from './NodeTitle';
import rcTreeMotion from './rcTreeMotion';
import renderSwitcherIcon from './renderSwitcherIcon';
import { TreeClasses, TreeRoot } from './TreeRoot';
import { getRCTree } from './util';
/**
 * Wrapper for rc-tree <Tree>, with Material Design style.
 *
 * @see https://github.com/react-component/tree#tree-props
 *
 * rc-tree expects a treeData prop containing a tree of nodes with key, title,
 * and children fields.
 *
 * @example // treeData format
 *
 * [
 *   { key: '1', title: 'foo1', children: [
 *     { key: '3', title: 'foo3', children: [
 *       { key: '5', title: 'foo5', children: [] },
 *     ] },
 *     { key: '4', title: 'foo4', children: [] },
 *   ] },
 *   { key: '2', title: 'foo2', children: [] },
 * ]
 *
 * @example // usage
 *
 * import { Tree } from '@react-admin/ra-tree';
 * import treeData from './treeData';
 *
 * export const SimpleTree: FC = () => <Tree treeData={treeData} />;
 */
export var Tree = function (props) {
    var allowDropProp = props.allowDrop, checkedKeys = props.checkedKeys, checkStrictly = props.checkStrictly, className = props.className, data = props.data, defaultCheckedKeys = props.defaultCheckedKeys, defaultExpandedKeys = props.defaultExpandedKeys, defaultSelectedKeys = props.defaultSelectedKeys, expandedKeys = props.expandedKeys, _a = props.hideRootNodes, hideRootNodes = _a === void 0 ? false : _a, _b = props.showIcon, showIcon = _b === void 0 ? false : _b, _c = props.showLine, showLine = _c === void 0 ? false : _c, _d = props.motion, motion = _d === void 0 ? false : _d, nodeActions = props.nodeActions, selectedKeys = props.selectedKeys, switcherIcon = props.switcherIcon, sx = props.sx, titleField = props.titleField, onExpand = props.onExpand, onSelect = props.onSelect, onCheck = props.onCheck, onClick = props.onClick, onDrop = props.onDrop, rest = __rest(props, ["allowDrop", "checkedKeys", "checkStrictly", "className", "data", "defaultCheckedKeys", "defaultExpandedKeys", "defaultSelectedKeys", "expandedKeys", "hideRootNodes", "showIcon", "showLine", "motion", "nodeActions", "selectedKeys", "switcherIcon", "sx", "titleField", "onExpand", "onSelect", "onCheck", "onClick", "onDrop"]);
    var translate = useTranslate();
    var resource = useResourceContext(props);
    var getRecordRepresentation = useGetRecordRepresentation(resource);
    // prepare data structure for rc-tree
    // add key and title, and turn to recursive DataNode[] format
    var treeData = React.useMemo(function () {
        return data
            ? getRCTree(data, titleField || getRecordRepresentation, translate('ra-tree.new_node'), hideRootNodes)
            : undefined;
    }, [data, getRecordRepresentation, hideRootNodes, titleField, translate]);
    var dataById = React.useMemo(function () {
        var _a;
        return (_a = data === null || data === void 0 ? void 0 : data.reduce(function (acc, curr) {
            acc[curr.id] = curr;
            return acc;
        }, {})) !== null && _a !== void 0 ? _a : {};
    }, [data]);
    var renderTitle = React.useCallback(function (data) { return (_jsx(NodeTitle, { data: data, nodeActions: nodeActions })); }, [nodeActions]);
    var renderSwitcherIconCallback = React.useCallback(function (nodeProps) {
        return renderSwitcherIcon(TreeClasses.smallIcon, switcherIcon, showLine, nodeProps);
    }, [switcherIcon, showLine]);
    var handleExpand = useEvent(function (expandedKeys, info) {
        if (!onExpand) {
            return;
        }
        var record = data.find(function (record) { return record.id === info.node.id; });
        if (!record) {
            throw new Error("Record with id ".concat(info.node.id, " not found in data"));
        }
        var isExpanded = expandedKeys.includes(String(record.id));
        // rc-tree only deals with string keys but the data might use numbers as ids
        // find all tree records that matches the expandedKeys and return their ids
        return onExpand(info.nativeEvent, data.filter(function (record) { return expandedKeys.includes(String(record.id)); }), record, isExpanded);
    });
    var handleSelect = useEvent(function (selectedKeys, info) {
        if (!onSelect) {
            return;
        }
        var record = data.find(function (record) { return record.id === info.node.id; });
        if (!record) {
            throw new Error("Record with id ".concat(info.node.id, " not found in data"));
        }
        var isSelected = selectedKeys.includes(String(record.id));
        // rc-tree only deals with string keys but the data might use numbers as ids
        // find all tree records that matches the expandedKeys and return their ids
        return onSelect(info.nativeEvent, data.filter(function (record) {
            return selectedKeys.includes(String(record.id));
        })[0], record, isSelected);
    });
    var handleCheck = useEvent(function (checkedKeys, info) {
        if (!onCheck) {
            return;
        }
        var record = data.find(function (record) { return record.id === info.node.id; });
        if (!record) {
            throw new Error("Record with id ".concat(info.node.id, " not found in data"));
        }
        // rc-tree only deals with string keys but the data might use numbers as ids
        // find all tree records that matches the expandedKeys and return their ids
        var selectedNodes = info.checkedNodes.filter(function (node) {
            return checkStrictly
                ? checkedKeys.checked.includes(node.key)
                : checkedKeys.includes(node.key);
        });
        var isSelected = selectedNodes.find(function (node) { return node.id === record.id; }) != null;
        onCheck(info.nativeEvent, data.filter(function (record) {
            return selectedNodes.find(function (node) { return node.id === record.id; });
        }), record, isSelected);
    });
    var handleClick = useEvent(function (event, treeNode) {
        if (!onClick)
            return;
        var id = treeNode.id;
        var node = data.find(function (node) { return node.id === id; });
        if (!node) {
            throw new Error("Node with id ".concat(id, " not found"));
        }
        onClick(event, node);
    });
    var handleDrop = useEvent(function (info) {
        var _a, _b;
        if (!onDrop)
            return;
        var dropKey = info.node.id;
        var dragKey = info.dragNode.id;
        var dropPos = info.node.pos.split('-');
        var dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
        var dragObject = dataById[dragKey];
        var targetObject = dataById[dropKey];
        if (!info.dropToGap) {
            // { source, parent, position }
            // Drop on a node
            // move dragObject as first child of targetObject
            onDrop(info.event, {
                source: dragObject,
                destination: targetObject,
                position: 0,
                type: 'child',
            });
        }
        else if ((info.node.children || []).length > 0 && // Has children
            info.node.expanded && // Is expanded
            dropPosition === 1 // On the bottom gap
        ) {
            // drop on the bottom gap
            // move dragObject as last child of targetObject
            // { source, parent, position, type: 'child' }
            onDrop(info.event, {
                source: dragObject,
                destination: targetObject,
                position: (_b = (_a = targetObject.children) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
                type: 'child',
            });
        }
        else {
            // drop between nodes
            // move dragObject to sibling targetPosition of targetObject
            // { source, parent, position, type: 'sibling' }
            onDrop(info.event, {
                source: dragObject,
                destination: targetObject,
                position: info.dropPosition,
                type: 'sibling',
            });
        }
    });
    var allowDrop = useEvent(function (options) {
        if (!allowDropProp) {
            return true;
        }
        var dropNode = options.dropNode, dragNode = options.dragNode, dropPosition = options.dropPosition;
        var dragObject = dataById[dragNode.key.toString()];
        var targetObject = dataById[dropNode.key.toString()];
        return allowDropProp({
            source: dragObject,
            destination: targetObject,
            position: dropPosition,
            type: dropNode.children ? 'child' : 'sibling',
        });
    });
    return (_jsx(TreeRoot, __assign({ allowDrop: allowDrop, treeData: treeData, className: className, checkStrictly: checkStrictly, sx: sx, switcherIcon: renderSwitcherIconCallback, titleRender: renderTitle, expandAction: "click", motion: motion === false
            ? undefined
            : motion === true
                ? rcTreeMotion
                : motion, showLine: showLine, showIcon: showIcon, dropIndicatorRender: DropIndicator, defaultCheckedKeys: defaultCheckedKeys ? defaultCheckedKeys.map(String) : undefined }, (checkedKeys && Array.isArray(checkedKeys)
        ? { checkedKeys: checkedKeys.map(String) }
        : undefined), { defaultExpandedKeys: defaultExpandedKeys
            ? defaultExpandedKeys.map(String)
            : undefined }, (expandedKeys
        ? { expandedKeys: expandedKeys.map(String) }
        : undefined), { defaultSelectedKeys: defaultSelectedKeys
            ? defaultSelectedKeys.map(String)
            : undefined }, (selectedKeys
        ? { selectedKeys: selectedKeys.map(String) }
        : undefined), { onExpand: handleExpand, onSelect: handleSelect, onClick: handleClick, onCheck: handleCheck, onDrop: handleDrop }, rest)));
};
